import React, { useEffect } from 'react';
import { X, Send, Sparkles, Brain, Zap, Trash2, BookOpen, Target } from 'lucide-react';
import WorkflowAnalysisModal from './WorkflowAnalysisModal';
import { useAssistantLogic } from '../hooks/useAssistantLogic';
import { MessageBubble } from './assistant/MessageBubble';

export const MagnusAssistant: React.FC = () => {
    const {
        isOpen, setIsOpen,
        showIntroBubble, setShowIntroBubble,
        isPrimeMode, setIsPrimeMode,
        isThinking,
        isRateLimited,
        messages,
        inputValue, setInputValue,
        handleSend,
        handleClear,
        messagesEndRef,
        highlightTarget,
        showWorkflowAnalysis, setShowWorkflowAnalysis,
        handleAction
    } = useAssistantLogic();

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
            {/* Intro Bubble */}
            {!isOpen && showIntroBubble && (
                <div className="fixed bottom-24 right-6 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-xl border-l-4 border-purple-500 max-w-xs z-40 animate-fade-in">
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-purple-600 dark:text-purple-400">Hi, I'm Magnus! 👋</h4>
                        <button onClick={(e) => { e.stopPropagation(); setShowIntroBubble(false); }} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        I have two modes:
                        <br /><span className="font-semibold text-blue-500">⚡ Basic:</span> I'll help you navigate.
                        <br /><span className="font-semibold text-yellow-500">🚀 Prime:</span> I'll analyze your data deeply.
                    </p>

                    <div className="absolute bottom-[-6px] right-8 w-4 h-4 bg-white dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 transform rotate-45"></div>
                </div>
            )}

            {/* Floating Magnus Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center group"
                title="Magnus"
            >
                <img
                    src="/magnus-icon.png"
                    alt="Magnus"
                    className="w-10 h-10 group-hover:scale-110 transition-transform"
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                />
                <Brain className="hidden w-8 h-8" />
                {!isOpen && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse"></span>
                )}
            </button>

            {/* Magnus Chat Panel */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 w-96 h-[600px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border-2 border-purple-200 dark:border-purple-700">
                    {/* Header */}
                    <div className={`bg-gradient-to-r p-4 flex items-center justify-between transition-colors duration-500 ${isPrimeMode ? 'from-slate-900 via-purple-900 to-slate-800' : 'from-purple-500 to-blue-600'}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                {isPrimeMode ? <Zap className="text-yellow-400" size={20} /> : <Sparkles className="text-white" size={20} />}
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-lg">Magnus</h3>
                                <p className="text-purple-100 text-xs">Reliability Assistant</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setIsPrimeMode(!isPrimeMode)}
                                className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${isPrimeMode
                                    ? 'bg-yellow-400 text-purple-900 border-yellow-300 shadow-[0_0_10px_rgba(250,204,21,0.5)]'
                                    : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                                    }`}
                            >
                                {isPrimeMode ? '🚀 PRIME' : '⚡ BASIC'}
                            </button>
                            <button
                                onClick={handleClear}
                                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                                title="Clear Chat"
                            >
                                <Trash2 size={16} />
                            </button>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Rate Limit Warning Banner */}
                    {isRateLimited && (
                        <div className="bg-gradient-to-r from-amber-500 to-red-500 px-4 py-2 flex items-center gap-2 text-white text-xs">
                            <span className="animate-pulse">⚠️</span>
                            <div className="flex-1">
                                <strong>Rate Limit Active</strong> - Running on Basic Magnus Mode
                            </div>
                            <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-bold">BASIC</span>
                        </div>
                    )}

                    {/* Quick Tips Bar */}
                    <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
                        <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
                            <BookOpen size={14} />
                            <span className="font-medium">Try: "How do I reduce RPN?" or "Where do I add tests?"</span>
                        </div>
                    </div>

                    {/* Workflow Analysis Button - Only show in Prime Mode */}
                    {isPrimeMode && (
                        <div className="px-4 py-3 border-b border-border bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
                            <button
                                onClick={() => setShowWorkflowAnalysis(true)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-primary/80 text-white rounded-lg font-medium hover:shadow-lg transition-all hover:scale-[1.02]"
                            >
                                <Target size={20} />
                                🎯 Analyze My Workflow
                                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full ml-1">5 AI Agents</span>
                            </button>
                        </div>
                    )}

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
                        {messages.map((msg, idx) => (
                            <MessageBubble
                                key={idx}
                                message={msg}
                                onAction={handleAction}
                            />
                        ))}
                        {isThinking && (
                            <div className="flex justify-start">
                                <div className="bg-white dark:bg-slate-700 rounded-xl p-3 shadow-md border-2 border-purple-200 animate-pulse">
                                    <span className="text-xs text-purple-600 dark:text-purple-300 font-bold flex items-center gap-2">
                                        <Sparkles size={12} className="animate-spin" />
                                        Thinking...
                                    </span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-4 bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={isThinking ? "Thinking..." : "Ask Magnus anything..."}
                                disabled={isThinking}
                                className="flex-1 px-4 py-2 rounded-lg border-2 border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all disabled:opacity-50 disabled:cursor-wait"
                            />
                            <button
                                onClick={handleSend}
                                disabled={!inputValue.trim() || isThinking}
                                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Highlight Overlay */}
            {highlightTarget && (
                <style>{`
                    ${highlightTarget} {
                        animation: pulse-highlight 2s ease-in-out;
                        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5);
                        border-radius: 4px;
                    }
                    @keyframes pulse-highlight {
                        0%, 100% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5); }
                        50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.8); }
                    }
                `}</style>
            )}

            {/* Workflow Analysis Modal */}
            <WorkflowAnalysisModal
                isOpen={showWorkflowAnalysis}
                onClose={() => setShowWorkflowAnalysis(false)}
            />
        </>
    );
};


